import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/objectWithoutPropertiesLoose";
const _excluded = ["found", "checkedBatches"];
import { DomainsApiClient } from '@wix/domains-serverless-interface/react-kit';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useInfiniteQuery } from 'react-query';
import { tldOfferWidgetApiClient } from './tldOfferWidget.apiClient';
import { ELIGIBILITY_MAX_BATCHES, TOP_TLDS_COUNT } from './tldOfferWidget.constants';
import { getBatch, getNextBatchIndex, shouldCheckAnotherBatch, shouldFoldStrip, toPurchasableTlds } from './tldOfferWidget.utils';
const checkBatches = async _ref => {
  let _ref$found = _ref.found,
    found = _ref$found === void 0 ? [] : _ref$found,
    _ref$checkedBatches = _ref.checkedBatches,
    checkedBatches = _ref$checkedBatches === void 0 ? 0 : _ref$checkedBatches,
    params = _objectWithoutPropertiesLoose(_ref, _excluded);
  const bulkCheck = params.bulkCheck,
    sld = params.sld,
    candidates = params.candidates,
    batchIndex = params.batchIndex,
    minTlds = params.minTlds,
    maxBatches = params.maxBatches;
  const _await$bulkCheck = await bulkCheck({
      domains: getBatch(candidates, batchIndex).map(tld => `${sld}.${tld}`)
    }),
    _await$bulkCheck$resu = _await$bulkCheck.results,
    results = _await$bulkCheck$resu === void 0 ? [] : _await$bulkCheck$resu;
  const tlds = [...found, ...toPurchasableTlds(results)];
  const nextBatchIndex = getNextBatchIndex(batchIndex + 1, candidates.length);
  const shouldContinue = nextBatchIndex !== undefined && shouldCheckAnotherBatch({
    foundCount: tlds.length,
    checkedBatches: checkedBatches + 1,
    minTlds,
    maxBatches,
    hasMoreCandidates: true
  });
  return shouldContinue ? checkBatches({
    ...params,
    batchIndex: nextBatchIndex,
    found: tlds,
    checkedBatches: checkedBatches + 1
  }) : {
    tlds,
    nextBatchIndex
  };
};
export const useTldOffersData = function (domain, _temp) {
  let _ref2 = _temp === void 0 ? {} : _temp,
    _ref2$withPrices = _ref2.withPrices,
    withPrices = _ref2$withPrices === void 0 ? true : _ref2$withPrices;
  const _tldOfferWidgetApiCli = tldOfferWidgetApiClient.hooks.useTldCandidates({
      domain
    }, Boolean(domain), {
      useErrorBoundary: false
    }),
    candidatesData = _tldOfferWidgetApiCli.data,
    isCandidatesLoading = _tldOfferWidgetApiCli.isLoading;
  const sld = (candidatesData == null ? void 0 : candidatesData.sld) ?? '';
  const candidates = (candidatesData == null ? void 0 : candidatesData.candidates) ?? [];
  const topTlds = (candidatesData == null ? void 0 : candidatesData.topTlds) ?? [];
  const _DomainsApiClient$use = DomainsApiClient.useQueries(),
    bulkCheckDomainsAvailability = _DomainsApiClient$use.bulkCheckDomainsAvailability;
  const _useInfiniteQuery = useInfiniteQuery({
      queryKey: ['tldOfferBatches', domain],
      enabled: candidates.length > 0,
      staleTime: Infinity,
      cacheTime: Infinity,
      queryFn: _ref3 => {
        let _ref3$pageParam = _ref3.pageParam,
          pageParam = _ref3$pageParam === void 0 ? 0 : _ref3$pageParam;
        const isFirstPage = pageParam === 0;
        return checkBatches({
          bulkCheck: bulkCheckDomainsAvailability,
          sld,
          candidates,
          batchIndex: pageParam,
          minTlds: isFirstPage ? TOP_TLDS_COUNT : 1,
          maxBatches: isFirstPage ? ELIGIBILITY_MAX_BATCHES : Infinity
        });
      },
      getNextPageParam: lastPage => lastPage.nextBatchIndex
    }),
    data = _useInfiniteQuery.data,
    isBatchLoading = _useInfiniteQuery.isLoading,
    hasNextPage = _useInfiniteQuery.hasNextPage,
    isFetchingNextPage = _useInfiniteQuery.isFetchingNextPage,
    fetchNextPage = _useInfiniteQuery.fetchNextPage;
  const pages = (data == null ? void 0 : data.pages) ?? [];
  const offers = pages.flatMap(_ref4 => {
    let tlds = _ref4.tlds;
    return tlds;
  });
  const _tldOfferWidgetApiCli2 = tldOfferWidgetApiClient.hooks.useTldOfferPrices({
      tldBatches: pages.map(_ref5 => {
        let tlds = _ref5.tlds;
        return tlds;
      })
    }, withPrices && offers.length > 0, {
      useErrorBoundary: false,
      keepPreviousData: true
    }),
    priceData = _tldOfferWidgetApiCli2.data,
    isPricingLoading = _tldOfferWidgetApiCli2.isFetching;
  const pricesByDomain = new Map(((priceData == null ? void 0 : priceData.displayPrices) ?? []).map(_ref6 => {
    let tld = _ref6.tld,
      pricing = _ref6.pricing;
    return [`${sld}.${tld}`, pricing];
  }));
  const isLoading = isCandidatesLoading || candidates.length > 0 && isBatchLoading;
  return {
    sld,
    offers,
    isLoading,
    isFolded: !isLoading && shouldFoldStrip(offers, topTlds),
    isEligible: offers.length >= TOP_TLDS_COUNT,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
    pricesByDomain,
    isPricingLoading
  };
};
//# sourceMappingURL=useTldOffersData.js.map